import Choices from 'choices.js';
import SidePanel from './sidepanel';

// Spread a Choices.js v11 classname (string or string[]) onto a classList.
function applyClasses(el, classNames) {
  [].concat(classNames).forEach(c => c && el.classList.add(c));
}

const Tags = {
  start() {
    SidePanel.onUpdate($sidepanel => {
      const $tagsPicker = $sidepanel.find('.js-tags-picker');
      if ($tagsPicker.length > 0) {
        this.hookUpChoicesPlugin($tagsPicker[0]);
      }
    });
  },

  hookUpChoicesPlugin(element) {
    const choices = new Choices(element, {
      searchEnabled: false,
      removeItemButton: true,
      // Choices.js v11: callbackOnCreateTemplates receives (strToEl, escapeForTemplate, getClassNames).
      // Each returned template function receives (TemplateOptions, ChoiceFull, ...).
      // TemplateOptions.classNames are string[] arrays in v11.
      // ChoiceFull.customProperties is already a parsed object (parseCustomProperties ran on mount).
      callbackOnCreateTemplates() {
        return {
          // Renders a selected "chip" item inside the input area.
          item(
            { classNames: { item, button, highlightedState, itemSelectable, placeholder } },
            choice,
            removeItemButton
          ) {
            const div = document.createElement('div');
            applyClasses(div, item);

            const labelSpan = document.createElement('span');
            labelSpan.className = 'tag__label';
            labelSpan.innerHTML = choice.label;
            div.appendChild(labelSpan);

            Object.assign(div.dataset, {
              item: '',
              id: choice.id,
              value: choice.value,
            });

            // Persist customProperties on the rendered element so downstream
            // code can still read it if needed.
            if (choice.customProperties) {
              div.dataset.customProperties =
                typeof choice.customProperties === 'string'
                  ? choice.customProperties
                  : JSON.stringify(choice.customProperties);
            }

            if (choice.active) div.setAttribute('aria-selected', 'true');
            if (choice.disabled) div.setAttribute('aria-disabled', 'true');

            if (choice.placeholder) {
              applyClasses(div, placeholder);
              div.dataset.placeholder = '';
            }

            applyClasses(div, choice.highlighted ? highlightedState : itemSelectable);

            // Bobcat: colour the chip. In v11 customProperties is a parsed object.
            const props =
              choice.customProperties && typeof choice.customProperties === 'object'
                ? choice.customProperties : null;
            const color = props?.color;
            if (color) {
              div.classList.add('tag', `tag--${color}`);
              div.style.fontWeight = 'normal';
              if (props.textColor) div.style.color = props.textColor;
            }

            if (removeItemButton) {
              if (choice.disabled) {
                [].concat(itemSelectable).forEach(c => c && div.classList.remove(c));
              }
              div.dataset.deletable = '';

              const REMOVE_ITEM_TEXT = 'Remove item';
              const removeButton = document.createElement('button');
              removeButton.type = 'button';
              applyClasses(removeButton, button);
              removeButton.innerHTML = REMOVE_ITEM_TEXT;
              removeButton.setAttribute('aria-label', `${REMOVE_ITEM_TEXT}: '${choice.value}'`);
              removeButton.dataset.button = '';
              div.appendChild(removeButton);
            }

            return div;
          },

          // Renders a row in the dropdown list.
          choice(
            { classNames: { item, itemChoice, itemSelectable, selectedState, itemDisabled, placeholder } },
            choice,
            selectText
          ) {
            const div = document.createElement('div');

            if (choice.elementId) div.id = choice.elementId;

            applyClasses(div, item);
            applyClasses(div, itemChoice);

            // Bobcat: render label as a coloured tag span.
            const props =
              choice.customProperties && typeof choice.customProperties === 'object'
                ? choice.customProperties : null;
            const color = props?.color;
            const tag = document.createElement('span');
            tag.innerHTML = choice.label;
            tag.className = `tag${color ? ` tag--${color}` : ''}`;
            if (color) {
              tag.style.fontWeight = 'normal';
              if (props.textColor) tag.style.color = props.textColor;
            }
            div.appendChild(tag);

            if (choice.selected) applyClasses(div, selectedState);
            if (choice.placeholder) applyClasses(div, placeholder);

            div.setAttribute(
              'role',
              choice.group && choice.group.id > 0 ? 'treeitem' : 'option'
            );

            Object.assign(div.dataset, {
              choice: '',
              id: choice.id,
              value: choice.value,
              selectText,
            });

            if (choice.disabled) {
              applyClasses(div, itemDisabled);
              div.dataset.choiceDisabled = '';
              div.setAttribute('aria-disabled', 'true');
            } else {
              applyClasses(div, itemSelectable);
              div.dataset.choiceSelectable = '';
            }

            return div;
          },
        };
      },
    });
  },
};

export default Tags;
